import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useBooking } from '../../context/BookingContext';
import { useReview } from '../../context/ReviewContext';
import { useAuth } from '../../context/AuthContext';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';
import { getProfileImageWithFallback } from '../../utils/profileImageUtils';
import { Link } from 'react-router-dom';
import ReviewForm from '../Review/ReviewForm';

// ฟังก์ชันแสดงเวลาในรูปแบบที่อ่านง่าย (24 ชั่วโมง)
const displayTime = (time) => {
  if (!time) return '00:00';
  // แสดงเป็นรูปแบบ 24 ชั่วโมง พร้อมไอคอนนาฬิกา
  return `🕐 ${time}`;
};

const BookingHistory = () => {
  const { bookings, loading } = useBooking();
  const { canReviewBooking } = useReview();
  const { currentUser } = useAuth();
  const [tripPlanDetails, setTripPlanDetails] = useState({});
  const [loadingTripPlan, setLoadingTripPlan] = useState({});
  const [girlDataCache, setGirlDataCache] = useState({});
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  
  // Filter states
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [showFilters, setShowFilters] = useState(false);

  const loadGirlData = useCallback(async (girlId) => {
    if (!girlId || girlDataCache[girlId]) return girlDataCache[girlId];
    
    try {
      const girlRef = doc(db, 'girls', girlId);
      const girlSnap = await getDoc(girlRef);
      
      if (girlSnap.exists()) {
        const girlData = { id: girlId, ...girlSnap.data() };
        setGirlDataCache(prev => ({ ...prev, [girlId]: girlData }));
        return girlData;
      }
    } catch (error) {
      console.error('Error loading girl data:', error);
    }
    
    return null;
  }, [girlDataCache]);

  // Load girl data when bookings change
  useEffect(() => {
    const loadAllGirlData = async () => {
      const uniqueGirlIds = [...new Set(bookings.map(booking => booking.girlId).filter(Boolean))];
      
      for (const girlId of uniqueGirlIds) {
        if (!girlDataCache[girlId]) {
          await loadGirlData(girlId);
        }
      }
    };

    if (bookings.length > 0) {
      loadAllGirlData();
    }
  }, [bookings, loadGirlData]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'confirmed': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      case 'completed': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending': return 'รอยืนยัน';
      case 'confirmed': return 'ยืนยันแล้ว';
      case 'rejected': return 'ปฏิเสธ';
      case 'completed': return 'เสร็จสิ้น';
      default: return 'ไม่ทราบ';
    }
  };

  const loadTripPlanDetails = async (tripPlanId) => {
    if (!tripPlanId || tripPlanDetails[tripPlanId]) return;

    setLoadingTripPlan(prev => ({ ...prev, [tripPlanId]: true }));

    try {
      const tripPlanRef = doc(db, 'tripPlans', tripPlanId);
      const tripPlanSnap = await getDoc(tripPlanRef);
      
      if (tripPlanSnap.exists()) {
        setTripPlanDetails(prev => ({
          ...prev,
          [tripPlanId]: tripPlanSnap.data()
        }));
      }
    } catch (error) {
      console.error('Error loading trip plan details:', error);
    } finally {
      setLoadingTripPlan(prev => ({ ...prev, [tripPlanId]: false }));
    }
  };

  const handleTripPlanClick = (tripPlanId) => {
    if (tripPlanId) {
      loadTripPlanDetails(tripPlanId);
    }
  };

  // Filter and sort functions
  const getFilteredBookings = () => {
    let filtered = [...bookings];

    // Filter by status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(booking => booking.status === statusFilter);
    }

    // Filter by date
    if (dateFilter !== 'all') {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      
      filtered = filtered.filter(booking => {
        const bookingDate = new Date(booking.bookingDate);
        
        switch (dateFilter) {
          case 'today':
            return bookingDate >= today && bookingDate < new Date(today.getTime() + 24 * 60 * 60 * 1000);
          case 'thisWeek':
            const weekStart = new Date(today);
            weekStart.setDate(today.getDate() - today.getDay());
            const weekEnd = new Date(weekStart);
            weekEnd.setDate(weekStart.getDate() + 7);
            return bookingDate >= weekStart && bookingDate < weekEnd;
          case 'thisMonth':
            return bookingDate.getMonth() === now.getMonth() && bookingDate.getFullYear() === now.getFullYear();
          case 'past':
            return bookingDate < today;
          case 'future':
            return bookingDate >= today;
          default:
            return true;
        }
      });
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(booking => 
        booking.girlName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        booking.message.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (booking.tripPlanName && booking.tripPlanName.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // Sort bookings
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return new Date(b.createdAt.seconds * 1000) - new Date(a.createdAt.seconds * 1000);
        case 'oldest':
          return new Date(a.createdAt.seconds * 1000) - new Date(b.createdAt.seconds * 1000);
        case 'dateAsc':
          return new Date(a.bookingDate) - new Date(b.bookingDate);
        case 'dateDesc':
          return new Date(b.bookingDate) - new Date(a.bookingDate);
        case 'priceAsc':
          return (a.totalPrice || a.price || 0) - (b.totalPrice || b.price || 0);
        case 'priceDesc':
          return (b.totalPrice || b.price || 0) - (a.totalPrice || a.price || 0);
        case 'name':
          return a.girlName.localeCompare(b.girlName);
        default:
          return 0;
      }
    });

    return filtered;
  };

  const clearFilters = () => {
    setStatusFilter('all');
    setDateFilter('all');
    setSearchTerm('');
    setSortBy('newest');
  };

  const handleReviewClick = (booking) => {
    setSelectedBooking(booking);
    setShowReviewForm(true);
  };

  const handleReviewSuccess = () => {
    setShowReviewForm(false);
    setSelectedBooking(null);
    // Optionally refresh bookings or show success message
  };

  const filteredBookings = getFilteredBookings();

  if (loading) {
    return (
      <div className="p-6 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
        <p>กำลังโหลดประวัติการจอง...</p>
      </div>
    );
  }

  if (bookings.length === 0) {
    return (
      <div className="p-6 text-center">
        <div className="text-gray-500 text-lg mb-4">📝</div>
        <h3 className="text-xl font-semibold text-gray-700 mb-2">ยังไม่มีการจอง</h3>
        <p className="text-gray-500">เริ่มต้นจองคิวกับน้องๆ ได้เลย!</p>
      </div>
    );
  }

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold text-center mb-6">Hugkan - ประวัติการจองคิว</h2>
      
      {/* Filter and Search Section */}
      <div className="bg-white p-4 rounded-lg shadow border mb-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">🔍 กรองและค้นหา</h3>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition"
          >
            {showFilters ? 'ซ่อนตัวกรอง' : 'แสดงตัวกรอง'}
          </button>
        </div>

        {/* Search Bar */}
        <div className="mb-4">
          <input
            type="text"
            placeholder="ค้นหาตามชื่อน้อง, ข้อความ, หรือ Trip Plan..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* Filters */}
        {showFilters && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">สถานะ</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">ทั้งหมด</option>
                <option value="pending">รอยืนยัน</option>
                <option value="confirmed">ยืนยันแล้ว</option>
                <option value="rejected">ปฏิเสธ</option>
                <option value="completed">เสร็จสิ้น</option>
              </select>
            </div>

            {/* Date Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">วันที่</label>
              <select
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">ทั้งหมด</option>
                <option value="today">วันนี้</option>
                <option value="thisWeek">สัปดาห์นี้</option>
                <option value="thisMonth">เดือนนี้</option>
                <option value="past">ที่ผ่านมา</option>
                <option value="future">อนาคต</option>
              </select>
            </div>

            {/* Sort By */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">เรียงตาม</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="newest">ใหม่ล่าสุด</option>
                <option value="oldest">เก่าที่สุด</option>
                <option value="dateAsc">วันที่ (เก่า-ใหม่)</option>
                <option value="dateDesc">วันที่ (ใหม่-เก่า)</option>
                <option value="priceAsc">ราคา (ต่ำ-สูง)</option>
                <option value="priceDesc">ราคา (สูง-ต่ำ)</option>
                <option value="name">ชื่อน้อง (A-Z)</option>
              </select>
            </div>

            {/* Clear Filters */}
            <div className="flex items-end">
              <button
                onClick={clearFilters}
                className="w-full px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition"
              >
                ล้างตัวกรอง
              </button>
            </div>
          </div>
        )}

        {/* Results Summary */}
        <div className="text-sm text-gray-600">
          แสดง {filteredBookings.length} จาก {bookings.length} การจอง
          {(statusFilter !== 'all' || dateFilter !== 'all' || searchTerm) && (
            <span className="ml-2 text-blue-600">
              (กรองแล้ว)
            </span>
          )}
        </div>
      </div>
      
      {/* No Results Message */}
      {filteredBookings.length === 0 && bookings.length > 0 && (
        <div className="text-center py-8">
          <div className="text-gray-500 text-lg mb-4">🔍</div>
          <h3 className="text-xl font-semibold text-gray-700 mb-2">ไม่พบการจองที่ตรงกับเงื่อนไข</h3>
          <p className="text-gray-500 mb-4">ลองเปลี่ยนตัวกรองหรือคำค้นหา</p>
          <button
            onClick={clearFilters}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition"
          >
            ล้างตัวกรองทั้งหมด
          </button>
        </div>
      )}

      <div className="space-y-4">
        {filteredBookings.map((booking) => {
          // Get girl data from cache
          const girlData = girlDataCache[booking.girlId];
          
          // Debug logging
          console.log('🔍 Booking data:', {
            id: booking.id,
            girlId: booking.girlId,
            girlName: booking.girlName,
            girlData: girlData,
            tripPlanId: booking.tripPlanId,
            tripPlanName: booking.tripPlanName,
            tripPlanDetails: booking.tripPlanDetails
          });
          
          return (
          <div key={booking.id} className="bg-white p-4 rounded-lg shadow border">
            {/* Header with Girl Image and Name */}
            <div className="flex items-start space-x-4 mb-4">
              {/* Girl Profile Image */}
              <div className="flex-shrink-0">
                <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-gray-200 shadow-md">
                  <img
                    src={getProfileImageWithFallback(girlData, '/default-avatar.png')}
                    alt={booking.girlName}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.src = '/default-avatar.png';
                    }}
                  />
                </div>
              </div>
              
              {/* Girl Info and Status */}
              <div className="flex-1 min-w-0">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-semibold text-lg text-gray-900">{booking.girlName}</h3>
                    {girlData?.nickname && girlData.nickname !== booking.girlName && (
                      <p className="text-sm text-gray-600">({girlData.nickname})</p>
                    )}
                  </div>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(booking.status)}`}>
                    {getStatusText(booking.status)}
                  </span>
                </div>
                
                {/* Action Buttons */}
                <div className="mt-2 flex space-x-2">
                  {booking.girlId && (
                    <Link
                      to={`/girl/${booking.girlId}`}
                      className="inline-flex items-center px-3 py-1 bg-pink-500 text-white text-sm rounded-lg hover:bg-pink-600 transition-colors"
                    >
                      <span className="mr-1">👩</span>
                      ดู Profile
                    </Link>
                  )}
                  
                  {/* Review Button */}
                  {canReviewBooking(booking) && (
                    <button
                      onClick={() => handleReviewClick(booking)}
                      className="inline-flex items-center px-3 py-1 bg-yellow-500 text-white text-sm rounded-lg hover:bg-yellow-600 transition-colors"
                    >
                      <span className="mr-1">⭐</span>
                      รีวิว
                    </button>
                  )}
                </div>
              </div>
            </div>
            
            <div className="text-gray-600 text-sm space-y-1">
              <p><strong>ราคา:</strong> {booking.totalPrice || booking.price} เหรียญ</p>
              {booking.duration && (
                <p><strong>ระยะเวลา:</strong> {booking.duration} ชั่วโมง</p>
              )}
              {booking.bookingDate && (
                <p><strong>วันที่จอง:</strong> {new Date(booking.bookingDate).toLocaleDateString('th-TH', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}</p>
              )}
              {booking.bookingTime && (
                <p><strong>เวลา:</strong> {displayTime(booking.bookingTime)} - {displayTime(booking.endTime || 'ไม่ระบุ')}</p>
              )}
              
              {/* Trip Plan Information */}
              {(booking.tripPlanId || booking.tripPlanName || booking.tripPlanDetails) && (
                <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-blue-900 flex items-center">
                      🗺️ Trip Plan
                    </h4>
                    {booking.tripPlanId && (
                      <button
                        onClick={() => handleTripPlanClick(booking.tripPlanId)}
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                      >
                        {loadingTripPlan[booking.tripPlanId] ? 'กำลังโหลด...' : 'ดูรายละเอียด'}
                      </button>
                    )}
                  </div>
                  
                  <div className="text-blue-800">
                    <p className="font-medium">{booking.tripPlanName || 'Trip Plan'}</p>
                    {booking.tripPlanDetails && (
                      <div className="mt-2 text-sm space-y-1">
                        {booking.tripPlanDetails.tripLocation && (
                          <p>📍 {booking.tripPlanDetails.tripLocation}</p>
                        )}
                        {booking.tripPlanDetails.tripDate && (
                          <p>📅 {new Date(booking.tripPlanDetails.tripDate).toLocaleDateString('th-TH')}</p>
                        )}
                        {booking.tripPlanDetails.participantCount && (
                          <p>👥 {booking.tripPlanDetails.participantCount} คน</p>
                        )}
                        {booking.tripPlanDetails.requiredGirls && (
                          <p>👩 {booking.tripPlanDetails.requiredGirls} น้อง</p>
                        )}
                        {booking.tripPlanDetails.tripTheme && (
                          <p>🎭 {booking.tripPlanDetails.tripTheme}</p>
                        )}
                      </div>
                    )}
                  </div>
                  
                  {/* Expanded Trip Plan Details */}
                  {tripPlanDetails[booking.tripPlanId] && (
                    <div className="mt-3 pt-3 border-t border-blue-200">
                      <div className="text-sm space-y-1">
                        {tripPlanDetails[booking.tripPlanId].description && (
                          <p><strong>รายละเอียด:</strong> {tripPlanDetails[booking.tripPlanId].description}</p>
                        )}
                        {tripPlanDetails[booking.tripPlanId].specialRequirements && (
                          <p><strong>ข้อกำหนดพิเศษ:</strong> {tripPlanDetails[booking.tripPlanId].specialRequirements}</p>
                        )}
                        {tripPlanDetails[booking.tripPlanId].budget && (
                          <p><strong>งบประมาณ:</strong> {tripPlanDetails[booking.tripPlanId].budget} บาท</p>
                        )}
                        <p><strong>สร้างโดย:</strong> {tripPlanDetails[booking.tripPlanId].userName}</p>
                        <p><strong>สถานะ:</strong> 
                          <span className={`ml-2 px-2 py-1 rounded-full text-xs ${
                            tripPlanDetails[booking.tripPlanId].status === 'active' 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {tripPlanDetails[booking.tripPlanId].status === 'active' ? 'ใช้งาน' : tripPlanDetails[booking.tripPlanId].status}
                          </span>
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              <p><strong>ข้อความ:</strong> {booking.message}</p>
              <p><strong>วันที่สร้าง:</strong> {new Date(booking.createdAt.seconds * 1000).toLocaleString('th-TH')}</p>
              {booking.updatedAt && (
                <p><strong>อัปเดตล่าสุด:</strong> {new Date(booking.updatedAt.seconds * 1000).toLocaleString('th-TH')}</p>
              )}
            </div>
          </div>
        );
      })}
      </div>

      {/* Review Form Modal */}
      {showReviewForm && selectedBooking && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="max-w-2xl w-full max-h-screen overflow-y-auto">
            <ReviewForm
              booking={selectedBooking}
              revieweeId={selectedBooking.girlId}
              revieweeName={selectedBooking.girlName}
              revieweeRole="girl"
              onClose={() => {
                setShowReviewForm(false);
                setSelectedBooking(null);
              }}
              onSuccess={handleReviewSuccess}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingHistory;
